// In-text citation marker location for the claim-support pass (PRD §4b / §13).
//
// Sprint-9b scope: handle `[N]`-style numbered citations only (most medical
// journals). Author-year markers like "(Smith, 2020)" require name resolution
// across references; leaving that for a follow-up.

import type { ManuscriptDocument } from '../pdf/types';

export interface CitationMarker {
  /** Reference number(s) cited by this marker. */
  refNumbers: number[];
  /** Character offset into rawText where the marker starts. */
  offset: number;
  /** The marker text, e.g. "[3]" or "[3, 5-7]". */
  raw: string;
  /** The sentence containing the marker, lightly cleaned. */
  sentence: string;
}

const MARKER_RE = /\[(\d{1,3}(?:[\s,;]+\d{1,3})*(?:[\s]*[-–][\s]*\d{1,3})*)\]/g;

function expandRefList(inner: string): number[] {
  const out: number[] = [];
  // Split on commas / semicolons.
  for (const chunk of inner.split(/[,;]/)) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;
    const rangeMatch = trimmed.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (rangeMatch) {
      const a = Number.parseInt(rangeMatch[1], 10);
      const b = Number.parseInt(rangeMatch[2], 10);
      if (b > a && b - a <= 20) {
        // Reasonable range — expand.
        for (let i = a; i <= b; i++) out.push(i);
      }
    } else {
      const n = Number.parseInt(trimmed, 10);
      if (Number.isFinite(n) && n > 0 && n < 1000) out.push(n);
    }
  }
  return out;
}

function extractSentence(rawText: string, markerOffset: number, markerLen: number): string {
  // Look backwards to the previous sentence-ending punctuation (.!?), and
  // forwards to the next. Cap windows to keep prompts under control.
  const SENTENCE_END = /[.!?]\s/;
  const before = rawText.slice(Math.max(0, markerOffset - 600), markerOffset);
  const afterStart = markerOffset + markerLen;
  const after = rawText.slice(afterStart, Math.min(rawText.length, afterStart + 400));

  // Find the start: last sentence-end in `before`.
  let start = 0;
  const beforeMatches = [...before.matchAll(/[.!?]\s/g)];
  if (beforeMatches.length > 0) {
    const last = beforeMatches[beforeMatches.length - 1];
    start = (last.index ?? 0) + last[0].length;
  }
  const sentenceStart = Math.max(0, markerOffset - 600 + start);

  // Find the end: first sentence-end in `after`.
  const afterMatch = after.match(SENTENCE_END);
  const sentenceEnd = afterMatch && afterMatch.index !== undefined
    ? afterStart + afterMatch.index + 1
    : Math.min(rawText.length, afterStart + 400);

  return rawText.slice(sentenceStart, sentenceEnd).replace(/\s+/g, ' ').trim();
}

export function findCitationMarkers(doc: ManuscriptDocument): CitationMarker[] {
  // Restrict to the body, not the references section.
  const refsSpan = doc.sections.find((s) => s.section === 'references');
  const bodyEnd = refsSpan ? refsSpan.start : doc.rawText.length;
  const body = doc.rawText.slice(0, bodyEnd);

  const markers: CitationMarker[] = [];
  MARKER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MARKER_RE.exec(body)) !== null) {
    const refNumbers = expandRefList(m[1]);
    if (refNumbers.length === 0) continue;
    markers.push({
      refNumbers,
      offset: m.index,
      raw: m[0],
      sentence: extractSentence(body, m.index, m[0].length),
    });
  }
  return markers;
}

/**
 * Build a map from reference number → first-encountered citation marker in the
 * body. Used by the claim-support runner to find the manuscript sentence that
 * cites a given reference.
 */
export function citationByRefNumber(markers: CitationMarker[]): Map<number, CitationMarker> {
  const map = new Map<number, CitationMarker>();
  for (const m of markers) {
    for (const refN of m.refNumbers) {
      if (!map.has(refN)) map.set(refN, m);
    }
  }
  return map;
}
